import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

export type TemplateFolderModalMode = 'create' | 'rename' | 'delete';

export interface LomavisTemplateFolderModalLabels {
  createTitle: string;
  renameTitle: string;
  deleteTitle: string;
  folderName: string;
  cancel: string;
  create: string;
  save: string;
  delete: string;
  deleteMessage: string;
}

export interface LomavisTemplateFolderModalProps {
  show: boolean;
  mode: TemplateFolderModalMode;
  name: string;
  targetName?: string;
  labels: LomavisTemplateFolderModalLabels;
  validationMessage?: string;
  isSubmitting?: boolean;
  onNameChange: (name: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

/**
 * Organism: controlled folder create, rename, and delete confirmation modal.
 */
export const LomavisTemplateFolderModal: React.FC<
  LomavisTemplateFolderModalProps
> = ({
  show,
  mode,
  name,
  targetName,
  labels,
  validationMessage,
  isSubmitting = false,
  onNameChange,
  onCancel,
  onSubmit
}) => {
  const isDelete = mode === 'delete';
  const title =
    mode === 'create'
      ? labels.createTitle
      : mode === 'rename'
      ? labels.renameTitle
      : labels.deleteTitle;
  const submitLabel =
    mode === 'create'
      ? labels.create
      : mode === 'rename'
      ? labels.save
      : labels.delete;
  const cannotSubmit = isSubmitting || (!isDelete && name.trim().length === 0);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!cannotSubmit) onSubmit();
  };

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {isDelete ? (
            <>
              {targetName && (
                <p className="fw-semibold text-dark mb-2">{targetName}</p>
              )}
              <p className="text-muted mb-0">{labels.deleteMessage}</p>
            </>
          ) : (
            <Form.Group controlId="overlay-template-folder-name">
              <Form.Label>{labels.folderName}</Form.Label>
              <Form.Control
                autoFocus
                value={name}
                isInvalid={Boolean(validationMessage)}
                aria-invalid={Boolean(validationMessage)}
                disabled={isSubmitting}
                onChange={(event) => onNameChange(event.target.value)}
              />
              {validationMessage && (
                <Form.Control.Feedback type="invalid">
                  {validationMessage}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            size="sm"
            variant="white"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {labels.cancel}
          </Button>
          <Button
            type="submit"
            size="sm"
            variant={isDelete ? 'danger' : 'primary'}
            disabled={cannotSubmit}
          >
            {isSubmitting && (
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
            )}
            {submitLabel}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
